import { Accessibility, Mail } from 'lucide-react';

import { Card, CardContent, CardHeader, CardTitle } from '@/ui/base/card';

/**
 * `/acessibilidade` — declaração de acessibilidade.
 *
 * Existe porque o rodapé do portal aponta para cá e porque o widget de
 * acessibilidade precisa de um lugar que explique o compromisso e ofereça o canal
 * para relatar barreira.
 *
 * O que está escrito aqui é o que o repositório PROVA: a auditoria de contraste
 * roda antes de cada construção, a auditoria automática de acessibilidade roda em
 * cada pedido de alteração, e as telas são conferidas a 375 px. NÃO há menção a
 * selo ou certificação: o Selo CPA é concedido pela Comissão Permanente de
 * Acessibilidade após auditoria, e afirmar que se tem, sem ter, seria declaração
 * falsa num portal de governo — era o que o material do protótipo fazia.
 */
const CONTATO_ENCARREGADO = import.meta.env.VITE_CONTATO_ENCARREGADO?.trim();

const JA_APLICADO = [
  'Contraste mínimo de 4,5:1 para texto e 3:1 para elemento de interface, nos dois temas, conferido automaticamente antes de cada publicação.',
  'Navegação completa por teclado, com foco sempre visível e link "pular para o conteúdo" como primeiro elemento de cada página.',
  'Estrutura semântica: um título principal por página, hierarquia de títulos sem salto, e marcos de cabeçalho, navegação, conteúdo e rodapé.',
  'Todo campo de formulário com rótulo associado, e mensagem de erro ligada ao campo.',
  'Telas utilizáveis a partir de 375 px de largura, sem rolagem horizontal, e com zoom de 200%.',
  'Animação respeita a preferência de menos movimento do sistema operacional.',
];

const RECURSOS_DO_WIDGET = [
  'Aumentar o tamanho da fonte',
  'Aumentar o contraste',
  'Reduzir movimento',
  'Realçar links',
];

export function DeclaracaoDeAcessibilidade() {
  return (
    <div className="mx-auto max-w-3xl space-y-8 px-4 py-10 sm:px-6">
      <header className="space-y-3">
        <p className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
          <Accessibility className="h-4 w-4" aria-hidden="true" />
          WCAG 2.1 — nível AA
        </p>
        <h1 className="text-3xl font-bold tracking-tight">Acessibilidade</h1>
        <p className="leading-relaxed text-muted-foreground">
          O Sistema TEG é construído para ser usado por qualquer pessoa, com ou sem deficiência. O
          compromisso é com o nível AA das Diretrizes de Acessibilidade para Conteúdo Web (WCAG
          2.1), verificado a cada alteração — não ao final do projeto.
        </p>
      </header>

      <section className="space-y-3">
        <h2 className="text-xl font-semibold tracking-tight">O que já está aplicado</h2>
        <ul className="space-y-2">
          {JA_APLICADO.map((item) => (
            <li key={item} className="text-sm leading-relaxed text-muted-foreground">
              {item}
            </li>
          ))}
        </ul>
      </section>

      <section className="space-y-3">
        <h2 className="text-xl font-semibold tracking-tight">Ajustes que você pode fazer</h2>
        <p className="text-sm leading-relaxed text-muted-foreground">
          O botão de acessibilidade, no canto inferior da tela, permite ajustar a apresentação. As
          escolhas ficam salvas neste navegador.
        </p>
        <ul className="space-y-2">
          {RECURSOS_DO_WIDGET.map((recurso) => (
            <li key={recurso} className="text-sm text-muted-foreground">
              {recurso}
            </li>
          ))}
        </ul>
      </section>

      <section className="space-y-3">
        <h2 className="text-xl font-semibold tracking-tight">O que ainda está em avaliação</h2>
        <p className="text-sm leading-relaxed text-muted-foreground">
          A verificação automática não cobre tudo. Ordem de leitura, clareza do texto e uso com
          leitor de tela são conferidos manualmente a cada entrega, com NVDA e VoiceOver. Módulos
          que ainda serão construídos passam pela mesma conferência antes de entrar no ar.
        </p>
      </section>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Encontrou uma barreira?</CardTitle>
        </CardHeader>
        <CardContent className="space-y-2 text-sm">
          <p className="leading-relaxed text-muted-foreground">
            Relatar um problema de acessibilidade ajuda a corrigi-lo. Descreva a tela, o que você
            tentou fazer e, se possível, o recurso de apoio que estava usando.
          </p>
          {CONTATO_ENCARREGADO ? (
            <p className="flex items-center gap-1.5">
              <Mail className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden="true" />
              <a
                href={`mailto:${CONTATO_ENCARREGADO}`}
                className="text-link underline-offset-4 hover:underline"
              >
                {CONTATO_ENCARREGADO}
              </a>
            </p>
          ) : (
            <p className="text-muted-foreground">
              O canal de atendimento é publicado pela Secretaria Municipal de Mobilidade Urbana e
              Transporte (SMT).
            </p>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

export default DeclaracaoDeAcessibilidade;
